import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import ConfirmModal from "../../components/common/ConfirmModal";
import constants from "../../Constants";
import "../integrations/integrations.css";
import "./team.css";

const baseURL = constants.baseURL;

const INVITE_ROLES = [
  {
    value: "admin",
    label: "Admin",
    hint: "Invite teammates, edit business profile, pay invoices. No integrations access.",
  },
  {
    value: "member",
    label: "Member",
    hint: "Book trips and view shared trips and billing. Cannot pay or invite.",
  },
  {
    value: "viewer",
    label: "Viewer",
    hint: "Read-only - monitor trips and billing. Cannot book or pay.",
  },
];

const FALLBACK_ROLE_DEFINITIONS = [
  {
    value: "owner",
    label: "Owner",
    permissions: {
      bookTrips: true,
      viewOrgData: true,
      manageTeam: true,
      editBusiness: true,
      integrations: true,
      apiPlan: true,
      payBilling: true,
    },
  },
  {
    value: "admin",
    label: "Admin",
    permissions: {
      bookTrips: true,
      viewOrgData: true,
      manageTeam: true,
      editBusiness: true,
      integrations: false,
      apiPlan: false,
      payBilling: true,
    },
  },
  {
    value: "member",
    label: "Member",
    permissions: {
      bookTrips: true,
      viewOrgData: true,
      manageTeam: false,
      editBusiness: false,
      integrations: false,
      apiPlan: false,
      payBilling: false,
    },
  },
  {
    value: "viewer",
    label: "Viewer",
    permissions: {
      bookTrips: false,
      viewOrgData: true,
      manageTeam: false,
      editBusiness: false,
      integrations: false,
      apiPlan: false,
      payBilling: false,
    },
  },
];

const FALLBACK_PERMISSION_LABELS = [
  { key: "bookTrips", label: "Book trips" },
  { key: "viewOrgData", label: "View org trips & billing" },
  { key: "manageTeam", label: "Invite & manage team" },
  { key: "editBusiness", label: "Edit business profile" },
  { key: "integrations", label: "Integrations hub" },
  { key: "apiPlan", label: "Partner API plan" },
  { key: "payBilling", label: "Pay invoices (EFT)" },
];

function memberInitials(member) {
  const first = (member.firstname || "").trim();
  const last = (member.lastname || "").trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  const email = (member.email || "").trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return "?";
}

function memberDisplayName(member) {
  const name = [member.firstname, member.lastname].filter(Boolean).join(" ").trim();
  return name || member.email;
}

function avatarClass(role, status) {
  if (status === "pending") return "qt-member-avatar--pending";
  return `qt-member-avatar--${role || "member"}`;
}

class TeamPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      members: [],
      org: null,
      permissions: {},
      roleDefinitions: FALLBACK_ROLE_DEFINITIONS,
      permissionLabels: FALLBACK_PERMISSION_LABELS,
      inviteEmail: "",
      inviteRole: "member",
      inviting: false,
      error: "",
      success: "",
      roleUpdating: null,
      removing: null,
      accountPromocode: null,
      settingUp: false,
      showMatrix: true,
      confirmModal: null,
    };
  }

  componentDidMount() {
    document.title = "Team | QikTruck";
    if (!cookie.load("session-id-qt")) {
      window.location.href = "/login";
      return;
    }
    this.loadTeam();
  }

  authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  });

  loadTeam = () => {
    this.setState({ loading: true, error: "" });
    axios
      .get(`${baseURL}/merchant/team/members`, { headers: this.authHeaders() })
      .then((res) => {
        const permissions = res.data?.permissions || {};
        this.setState({
          loading: false,
          members: res.data?.members || [],
          org: res.data?.org || null,
          permissions,
          roleDefinitions: res.data?.roleDefinitions || FALLBACK_ROLE_DEFINITIONS,
          permissionLabels: res.data?.permissionLabels || FALLBACK_PERMISSION_LABELS,
          accountPromocode: permissions.accountPromocode || null,
        });
      })
      .catch(() => {
        this.setState({ loading: false, error: "Could not load team members." });
      });
  };

  refreshSession = () =>
    axios.post(`${baseURL}/merchant/session/refresh`, {}, { headers: this.authHeaders() }).then((res) => {
      if (res.data?.["session-id-qt"]) {
        cookie.save("session-id-qt", res.data["session-id-qt"], {
          path: "/",
          maxAge: 86400 * 180,
          secure: true,
        });
      }
    });

  sendInvite = (e) => {
    e.preventDefault();
    const email = this.state.inviteEmail.trim();
    if (!email) return;
    this.setState({ inviting: true, error: "", success: "" });
    axios
      .post(
        `${baseURL}/merchant/team/invites`,
        { email, role: this.state.inviteRole },
        { headers: this.authHeaders() }
      )
      .then((res) => {
        this.setState({
          inviting: false,
          inviteEmail: "",
          success: res.data?.message || "Invitation sent.",
        });
        this.loadTeam();
      })
      .catch((err) => {
        this.setState({
          inviting: false,
          error: err?.response?.data?.error || "Failed to send invitation.",
        });
      });
  };

  changeRole = (memberId, role) => {
    this.setState({ roleUpdating: memberId, error: "", success: "" });
    axios
      .put(`${baseURL}/merchant/team/members/${memberId}/role`, { role }, { headers: this.authHeaders() })
      .then(() => {
        this.setState({ roleUpdating: null, success: "Role updated." });
        this.loadTeam();
      })
      .catch((err) => {
        this.setState({
          roleUpdating: null,
          error: err?.response?.data?.error || "Could not update role.",
        });
      });
  };

  closeConfirmModal = () => {
    if (this.state.settingUp || this.state.removing) return;
    this.setState({ confirmModal: null });
  };

  askRemoveMember = (member) => {
    const isPending = member.status === "pending";
    this.setState({
      confirmModal: {
        type: "removeMember",
        member,
        title: isPending ? "Cancel invitation" : "Remove team member",
        message: isPending
          ? `Cancel the pending invitation for ${member.email}? They will no longer be able to join with that invite link.`
          : `Remove ${member.email} from your team? They will lose access to shared trips and billing.`,
        confirmLabel: isPending ? "Cancel invitation" : "Remove member",
        confirmColor: "danger",
      },
    });
  };

  askSetupTeam = () => {
    this.setState({
      confirmModal: {
        type: "setupTeam",
        title: "Set up team management",
        message:
          "Set up team management for your organisation? You will become the account owner and can invite colleagues who should see shared trips and billing.",
        confirmLabel: "Set up team",
        confirmColor: "primary",
      },
    });
  };

  confirmModalAction = () => {
    const { confirmModal } = this.state;
    if (!confirmModal) return;
    if (confirmModal.type === "setupTeam") {
      this.executeSetupTeam();
      return;
    }
    if (confirmModal.type === "removeMember" && confirmModal.member) {
      this.executeRemoveMember(confirmModal.member);
    }
  };

  executeRemoveMember = (member) => {
    this.setState({ removing: member.memberId, error: "", success: "" });
    axios
      .delete(`${baseURL}/merchant/team/members/${member.memberId}`, { headers: this.authHeaders() })
      .then(() => {
        this.setState({
          removing: null,
          confirmModal: null,
          success: member.status === "pending" ? "Invitation cancelled." : "Member removed.",
        });
        this.loadTeam();
      })
      .catch((err) => {
        this.setState({
          removing: null,
          confirmModal: null,
          error: err?.response?.data?.error || "Could not remove member.",
        });
      });
  };

  executeSetupTeam = () => {
    this.setState({ settingUp: true, error: "", success: "" });
    axios
      .post(`${baseURL}/merchant/team/setup`, {}, { headers: this.authHeaders() })
      .then((res) => {
        return this.refreshSession().then(() => res);
      })
      .then((res) => {
        this.setState({
          settingUp: false,
          confirmModal: null,
          success: res.data?.message || "Team set up. You can now invite colleagues.",
        });
        this.loadTeam();
      })
      .catch((err) => {
        this.setState({
          settingUp: false,
          confirmModal: null,
          error: err?.response?.data?.error || "Could not set up team.",
        });
      });
  };

  renderPermissionMatrix() {
    const { roleDefinitions, permissionLabels, showMatrix } = this.state;
    if (!showMatrix) return null;

    const roles = roleDefinitions.filter((r) => r.value !== "owner");

    return (
      <div className="qt-card mb-4">
        <div className="qt-card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="qt-section-title h5 mb-1">Roles & access</h4>
              <p className="text-muted small mb-0">
                Team membership controls what people see in the portal. Using a promocode on a trip does{" "}
                <strong>not</strong> grant these permissions.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-link btn-sm text-muted p-0"
              onClick={() => this.setState({ showMatrix: false })}
            >
              Hide
            </button>
          </div>
          <div className="qt-perm-matrix">
            <table className="qt-perm-table">
              <thead>
                <tr>
                  <th>Permission</th>
                  {roles.map((r) => (
                    <th key={r.value}>{r.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {permissionLabels.map((pl) => (
                  <tr key={pl.key}>
                    <td>{pl.label}</td>
                    {roles.map((r) => (
                      <td key={r.value + pl.key}>
                        {r.permissions?.[pl.key] ? (
                          <i className="mdi mdi-check-circle qt-perm-yes" aria-label="Yes" />
                        ) : (
                          <i className="mdi mdi-minus-circle-outline qt-perm-no" aria-label="No" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted small mt-3 mb-0">
            <strong>Owner</strong> has full access including integrations and Partner API plan. Only the owner
            should manage API keys and store connections.
          </p>
        </div>
      </div>
    );
  }

  renderYourAccess() {
    const { permissions } = this.state;
    const items = [
      { key: "canBookTrips", label: "Book trips" },
      { key: "canPayBilling", label: "Pay invoices" },
      { key: "canManageTeam", label: "Manage team" },
      { key: "canEditBusiness", label: "Edit business profile" },
      { key: "canManageIntegrations", label: "Integrations & API plan" },
    ];

    return (
      <div className="qt-sidebar-card">
        <div className="qt-sidebar-head">Your access</div>
        <div className="p-3">
          {permissions.teamRole ? (
            <span className={`qt-role-pill qt-role-pill--${permissions.teamRole} mb-3`}>
              {permissions.teamRole}
            </span>
          ) : permissions.canSetupTeam ? (
            <span className="qt-role-pill qt-role-pill--member mb-3">Solo account</span>
          ) : null}
          <ul className="qt-your-access-list">
            {items.map((item) => {
              const allowed = permissions[item.key] !== false;
              return (
                <li key={item.key}>
                  <i
                    className={
                      "mdi " + (allowed ? "mdi-check-circle" : "mdi-close-circle-outline")
                    }
                    aria-hidden="true"
                  />
                  {item.label}
                </li>
              );
            })}
          </ul>
          {permissions.canManageIntegrations ? (
            <a href="/integrations" className="btn btn-sm btn-outline-secondary w-100 mt-2">
              <i className="mdi mdi-link-variant me-1" />
              Open integrations
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  renderMemberRow(member) {
    const { permissions, roleUpdating, removing } = this.state;
    const canManage = permissions.canManageTeam;

    return (
      <div className="qt-member-row" key={member.memberId}>
        <div className={`qt-member-avatar ${avatarClass(member.role, member.status)}`}>
          {memberInitials(member)}
        </div>
        <div className="qt-member-main">
          <div className="qt-member-name">{memberDisplayName(member)}</div>
          <div className="qt-member-email">{member.email}</div>
        </div>
        <div className="qt-member-meta">
          <span className={`qt-role-pill qt-role-pill--${member.role}`}>{member.role}</span>
          <span
            className={
              "qt-status-pill " +
              (member.status === "pending" ? "qt-status-pill--pending" : "qt-status-pill--active")
            }
          >
            {member.status === "pending" ? "Pending invite" : "Active"}
          </span>
        </div>
        {canManage ? (
          <div className="qt-member-actions">
            {member.role !== "owner" && member.status === "active" ? (
              <Input
                type="select"
                bsSize="sm"
                value={member.role}
                disabled={roleUpdating === member.memberId}
                onChange={(e) => this.changeRole(member.memberId, e.target.value)}
              >
                {INVITE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Input>
            ) : null}
            {member.role !== "owner" ? (
              <button
                type="button"
                className="qt-btn-ghost-danger"
                disabled={removing === member.memberId}
                onClick={() => this.askRemoveMember(member)}
              >
                {member.status === "pending" ? "Cancel" : "Remove"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      loading,
      members,
      org,
      permissions,
      inviteEmail,
      inviteRole,
      inviting,
      error,
      success,
      accountPromocode,
      settingUp,
      showMatrix,
      confirmModal,
      removing,
    } = this.state;

    const canManage = permissions.canManageTeam;
    const canSetupTeam = permissions.canSetupTeam;
    const hasTeam = permissions.hasTeam;
    const activeCount = members.filter((m) => m.status === "active").length;
    const pendingCount = members.filter((m) => m.status === "pending").length;
    const showOrgCode = hasTeam || canSetupTeam;
    const orgLabel = showOrgCode ? accountPromocode || org?.promocode : null;

    const confirmBusy =
      (confirmModal?.type === "setupTeam" && settingUp) ||
      (confirmModal?.type === "removeMember" && removing === confirmModal?.member?.memberId);

    return (
      <div className="qt-integrations qt-team">
        <ConfirmModal
          isOpen={!!confirmModal}
          title={confirmModal?.title}
          message={confirmModal?.message}
          confirmLabel={confirmModal?.confirmLabel}
          confirmColor={confirmModal?.confirmColor || "primary"}
          confirming={confirmBusy}
          onCancel={this.closeConfirmModal}
          onConfirm={this.confirmModalAction}
        />
        <Container fluid>
          <div className="qt-page-head">
            <div className="qt-breadcrumb">
              <a href="/dashboard">Dashboard</a>
              <span> / Team</span>
            </div>
            <div className="qt-team-hero">
              <div>
                <h1 className="qt-page-title">
                  <i className="mdi mdi-account-group-outline" aria-hidden="true" />
                  Team
                </h1>
                <p className="qt-page-lead">
                  {hasTeam
                    ? "Manage who can book, view billing, and access integrations for your organisation."
                    : canSetupTeam
                      ? "Set up your team to invite colleagues with controlled access - separate from promocode use on trips."
                      : "Team access is by invitation only. Ask your organisation owner to invite you."}
                </p>
              </div>
              {hasTeam ? (
                <div className="qt-team-stat-row">
                  <div className="qt-team-stat">
                    <strong>{activeCount}</strong>
                    <span>Active</span>
                  </div>
                  {pendingCount > 0 ? (
                    <div className="qt-team-stat">
                      <strong>{pendingCount}</strong>
                      <span>Pending</span>
                    </div>
                  ) : null}
                  {orgLabel ? (
                    <div className="qt-site-chip">
                      Org code <code>{orgLabel}</code>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {error ? <Alert color="danger">{error}</Alert> : null}
          {success ? <Alert color="success">{success}</Alert> : null}

          <div className="qt-privacy-tip">
            <i className="mdi mdi-shield-lock-outline" aria-hidden="true" />
            <div>
              <strong>Your data stays private.</strong> People who use your promocode on a trip are{" "}
              <em>not</em> added to your team automatically. Only invited members with accepted access can
              see organisation trips, billing, and profiles.
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={8}>
                {canSetupTeam ? (
                  <div className="qt-setup-card">
                    <div className="qt-card-body">
                      <h4 className="qt-section-title h5 mb-2">Set up your team</h4>
                      <p className="text-muted mb-0">
                        Your account is linked to organisation code{" "}
                        <code>{accountPromocode}</code>. Confirm setup to become owner and invite colleagues.
                      </p>
                      <div className="qt-setup-steps">
                        <div className="qt-setup-step">
                          <strong>1. You become owner</strong>
                          Full control of team, billing, and integrations.
                        </div>
                        <div className="qt-setup-step">
                          <strong>2. Invite colleagues</strong>
                          Choose admin, member, or viewer roles per person.
                        </div>
                        <div className="qt-setup-step">
                          <strong>3. They accept by email</strong>
                          Access starts only after they accept - never from promocode alone.
                        </div>
                      </div>
                      <Button color="primary" disabled={settingUp} onClick={this.askSetupTeam}>
                        {settingUp ? <Spinner size="sm" /> : "Set up team"}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {!hasTeam && !canSetupTeam ? (
                  <div className="qt-card mb-4">
                    <div className="qt-card-body qt-empty-state">
                      <i className="mdi mdi-account-off-outline d-block" />
                      <h4 className="h5">No team yet</h4>
                      <p className="text-muted mb-0">
                        If you need access to an organisation&apos;s trips and billing, ask the account owner
                        to send you an invitation from their Team page.
                      </p>
                    </div>
                  </div>
                ) : null}

                {hasTeam ? (
                  <>
                    <div className="qt-card mb-4">
                      <div className="qt-card-body">
                        <h4 className="qt-section-title h5 mb-3">Members</h4>
                        {members.length === 0 ? (
                          <p className="text-muted mb-0">No team members yet.</p>
                        ) : (
                          <div className="qt-member-list">
                            {members.map((m) => this.renderMemberRow(m))}
                          </div>
                        )}
                      </div>
                    </div>

                    {canManage ? (
                      <div className="qt-card mb-4">
                        <div className="qt-card-body qt-invite-form">
                          <h4 className="qt-section-title h5 mb-2">Invite someone</h4>
                          <p className="text-muted small mb-3">
                            They receive a secure email link. Access begins only when they accept while signed
                            in with the invited email address.
                          </p>

                          <Label className="small text-muted text-uppercase">Choose a role</Label>
                          <div className="qt-role-picker">
                            {INVITE_ROLES.map((r) => (
                              <button
                                key={r.value}
                                type="button"
                                className={
                                  "qt-role-option" + (inviteRole === r.value ? " is-selected" : "")
                                }
                                onClick={() => this.setState({ inviteRole: r.value })}
                              >
                                <strong>{r.label}</strong>
                                <span>{r.hint}</span>
                              </button>
                            ))}
                          </div>

                          <Form onSubmit={this.sendInvite}>
                            <Row className="align-items-end">
                              <Col md={8}>
                                <FormGroup className="mb-md-0">
                                  <Label for="inviteEmail">Email address</Label>
                                  <Input
                                    id="inviteEmail"
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => this.setState({ inviteEmail: e.target.value })}
                                    placeholder="colleague@company.co.za"
                                    required
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={4}>
                                <Button color="primary" block disabled={inviting} className="mt-3 mt-md-0">
                                  {inviting ? <Spinner size="sm" /> : "Send invite"}
                                </Button>
                              </Col>
                            </Row>
                          </Form>
                        </div>
                      </div>
                    ) : (
                      <div className="qt-tip qt-tip--info mb-4">
                        <i className="mdi mdi-information-outline me-2" />
                        Only owners and admins can invite teammates. Contact your account owner if someone
                        needs access.
                      </div>
                    )}

                    {this.renderPermissionMatrix()}
                    {!showMatrix ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm ps-0 mb-4"
                        onClick={() => this.setState({ showMatrix: true })}
                      >
                        Show roles & access matrix
                      </button>
                    ) : null}
                  </>
                ) : null}
              </Col>

              <Col lg={4}>
                <div className="qt-sidebar">
                  {this.renderYourAccess()}
                  {permissions.canManageIntegrations ? (
                    <div className="qt-sidebar-card">
                      <div className="qt-sidebar-head">Developer access</div>
                      <div className="p-3 small text-muted">
                        <p className="mb-2">
                          As owner you manage integrations, API keys, and your Partner API subscription plan.
                        </p>
                        <a href="/partner-plans" className="d-block mb-2">
                          <i className="mdi mdi-package-variant me-1" />
                          Partner API plan
                        </a>
                        <a href="/integrations">
                          <i className="mdi mdi-link-variant me-1" />
                          Integrations hub
                        </a>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  }
}

export default TeamPage;
